import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useChat } from '@/contexts/ChatContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  MessageSquare, 
  Send, 
  Search,
  Phone,
  Video,
  Check,
  CheckCheck,
  ArrowLeft,
  Paperclip,
  Smile,
  Shield,
  Wifi,
  WifiOff
} from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { toast } from 'sonner';

const MessagesPage = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;
  const messagesEndRef = useRef(null);

  const {
    conversations,
    loadingConversations,
    fetchConversations,
    activeConversation,
    messages,
    loadingMessages,
    openConversation,
    closeConversation,
    sendMessage,
    startCall,
    wsConnected,
  } = useChat();

  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  const isDoctor = user?.role === 'doctor' || user?.type === 'doctor';

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchConversations();
    
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isAuthenticated, navigate, fetchConversations]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !activeConversation) return;

    setSending(true);
    try {
      await sendMessage(newMessage.trim());
      setNewMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleStartCall = async (type) => {
    try {
      await startCall(type);
    } catch (error) {
      toast.error(lang === 'ar' ? 'فشل في بدء المكالمة' : 'Failed to start call');
    }
  };

  const formatMessageTime = (date) => {
    const msgDate = new Date(date);
    if (isToday(msgDate)) {
      return format(msgDate, 'p', { locale });
    } else if (isYesterday(msgDate)) {
      return lang === 'ar' ? 'أمس' : 'Yesterday';
    }
    return format(msgDate, 'PP', { locale });
  };

  const filteredConversations = conversations.filter(conv =>
    conv.participant?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAuthenticated) return null;

  const showConversationsList = !isMobileView || !activeConversation;
  const showMessagesView = !isMobileView || activeConversation;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="messages-page">
      <div className="container mx-auto px-0 md:px-4 py-0 md:py-6">
        <Card className="md:rounded-lg rounded-none border-0 shadow-sm overflow-hidden dark:bg-slate-800">
          <div className="flex h-[calc(100vh-0px)] md:h-[calc(100vh-120px)]">
            {/* Conversations List */}
            {showConversationsList && (
              <div className={`${isMobileView ? 'w-full' : 'w-80'} border-r dark:border-slate-700 flex flex-col`}>
                {/* Search Header */}
                <div className="p-4 border-b dark:border-slate-700">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                      {lang === 'ar' ? 'الرسائل' : 'Messages'}
                    </h2>
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-green-500" title={lang === 'ar' ? 'مشفر' : 'Encrypted'} />
                      {wsConnected ? (
                        <Wifi className="h-4 w-4 text-green-500" />
                      ) : (
                        <WifiOff className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder={lang === 'ar' ? 'بحث...' : 'Search...'}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* End-to-end encryption notice */}
                <div className="px-4 py-2 bg-green-50 dark:bg-green-900/20 border-b dark:border-slate-700">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                    <Shield className="h-4 w-4" />
                    <p className="text-xs">
                      {lang === 'ar' 
                        ? 'الرسائل مشفرة من طرف إلى طرف' 
                        : 'Messages are end-to-end encrypted'}
                    </p>
                  </div>
                </div>

                {/* Conversations */}
                <ScrollArea className="flex-1">
                  {loadingConversations ? (
                    <div className="p-4 space-y-4">
                      {[1, 2, 3, 4].map(i => (
                        <div key={i} className="flex items-center gap-3">
                          <Skeleton className="h-12 w-12 rounded-full" />
                          <div className="flex-1">
                            <Skeleton className="h-4 w-3/4 mb-2" />
                            <Skeleton className="h-3 w-1/2" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : filteredConversations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
                      <MessageSquare className="h-12 w-12 mb-4 opacity-30" />
                      <p className="text-center">
                        {searchTerm 
                          ? (lang === 'ar' ? 'لا توجد نتائج' : 'No results found')
                          : (lang === 'ar' ? 'لا توجد محادثات بعد' : 'No conversations yet')
                        }
                      </p>
                      <p className="text-sm text-center mt-1 px-4">
                        {lang === 'ar' 
                          ? 'احجز موعداً لبدء محادثة'
                          : 'Book an appointment to start chatting'}
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y dark:divide-slate-700">
                      {filteredConversations.map(conv => (
                        <div
                          key={conv.id}
                          className={`p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${
                            activeConversation?.id === conv.id ? 'bg-teal-50 dark:bg-teal-900/20' : ''
                          }`}
                          onClick={() => openConversation(conv)}
                          data-testid={`conversation-${conv.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <Avatar className="h-12 w-12">
                              <AvatarImage src={conv.participant?.image} />
                              <AvatarFallback className="bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300">
                                {conv.participant?.name?.charAt(0) || '?'}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <h4 className="font-medium text-slate-900 dark:text-white truncate">
                                  {conv.participant?.type === 'Doctor' && !isDoctor && `${lang === 'ar' ? 'د. ' : 'Dr. '}`}
                                  {conv.participant?.name}
                                </h4>
                                {conv.last_message?.created_at && (
                                  <span className="text-xs text-slate-400">
                                    {formatMessageTime(conv.last_message.created_at)}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                                {conv.last_message?.content || (lang === 'ar' ? 'ابدأ المحادثة' : 'Start chatting')}
                              </p>
                            </div>
                            {conv.unread_count > 0 && (
                              <Badge className="bg-teal-600">{conv.unread_count}</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            )}

            {/* Messages View */}
            {showMessagesView && (
              <div className="flex-1 flex flex-col">
                {activeConversation ? (
                  <>
                    {/* Chat Header */}
                    <div className="p-4 border-b dark:border-slate-700 flex items-center justify-between bg-white dark:bg-slate-800">
                      <div className="flex items-center gap-3">
                        {isMobileView && (
                          <Button variant="ghost" size="icon" onClick={closeConversation}>
                            <ArrowLeft className="h-5 w-5" />
                          </Button>
                        )}
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={activeConversation.participant?.image} />
                          <AvatarFallback className="bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300">
                            {activeConversation.participant?.name?.charAt(0) || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h3 className="font-medium text-slate-900 dark:text-white">
                            {activeConversation.participant?.type === 'Doctor' && !isDoctor && `${lang === 'ar' ? 'د. ' : 'Dr. '}`}
                            {activeConversation.participant?.name}
                          </h3>
                          <p className="text-xs text-green-500 flex items-center gap-1">
                            <Shield className="h-3 w-3" />
                            {lang === 'ar' ? 'مشفر' : 'Encrypted'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleStartCall('audio')}
                          title={lang === 'ar' ? 'مكالمة صوتية' : 'Voice call'}
                        >
                          <Phone className="h-5 w-5" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleStartCall('video')}
                          title={lang === 'ar' ? 'مكالمة فيديو' : 'Video call'}
                        >
                          <Video className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>

                    {/* Messages */}
                    <ScrollArea className="flex-1 p-4">
                      {loadingMessages ? (
                        <div className="space-y-4">
                          {[1, 2, 3].map(i => (
                            <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                              <Skeleton className="h-12 w-48 rounded-2xl" />
                            </div>
                          ))}
                        </div>
                      ) : messages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-slate-500 dark:text-slate-400">
                          <MessageSquare className="h-12 w-12 mb-4 opacity-30" />
                          <p>{lang === 'ar' ? 'لا توجد رسائل بعد' : 'No messages yet'}</p>
                          <p className="text-sm mt-1">
                            {lang === 'ar' ? 'أرسل رسالة لبدء المحادثة' : 'Send a message to start the conversation'}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {messages.map(msg => (
                            <div
                              key={msg.id}
                              className={`flex ${msg.is_mine ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                                  msg.is_mine
                                    ? 'bg-teal-600 text-white rounded-br-none'
                                    : 'bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white rounded-bl-none'
                                }`}
                              >
                                {msg.message_type === 'call_ended' ? (
                                  <div className="flex items-center gap-2 text-sm">
                                    <Phone className="h-4 w-4" />
                                    <span>
                                      {msg.metadata?.call_type === 'video' 
                                        ? (lang === 'ar' ? 'مكالمة فيديو' : 'Video call')
                                        : (lang === 'ar' ? 'مكالمة صوتية' : 'Voice call')
                                      }
                                      {msg.metadata?.duration && ` - ${Math.floor(msg.metadata.duration / 60)}:${(msg.metadata.duration % 60).toString().padStart(2, '0')}`}
                                    </span>
                                  </div>
                                ) : (
                                  <p>{msg.content}</p>
                                )}
                                <div className={`flex items-center gap-1 mt-1 ${
                                  msg.is_mine ? 'justify-end' : 'justify-start'
                                }`}>
                                  <span className={`text-xs ${
                                    msg.is_mine ? 'text-teal-100' : 'text-slate-400'
                                  }`}>
                                    {format(new Date(msg.created_at), 'p', { locale })}
                                  </span>
                                  {msg.is_mine && (
                                    msg.read_at 
                                      ? <CheckCheck className="h-4 w-4 text-teal-100" />
                                      : <Check className="h-4 w-4 text-teal-100" />
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                          <div ref={messagesEndRef} />
                        </div>
                      )}
                    </ScrollArea>

                    {/* Message Input */}
                    <div className="p-4 border-t dark:border-slate-700 bg-white dark:bg-slate-800">
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" disabled>
                          <Paperclip className="h-5 w-5" />
                        </Button>
                        <Input
                          placeholder={lang === 'ar' ? 'اكتب رسالة...' : 'Type a message...'}
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                          className="flex-1"
                          disabled={sending}
                        />
                        <Button variant="ghost" size="icon" disabled>
                          <Smile className="h-5 w-5" />
                        </Button>
                        <Button 
                          onClick={handleSendMessage}
                          disabled={!newMessage.trim() || sending}
                          className="bg-teal-600 hover:bg-teal-700"
                        >
                          <Send className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center text-slate-500 dark:text-slate-400">
                      <MessageSquare className="h-16 w-16 mx-auto mb-4 opacity-30" />
                      <p className="text-lg font-medium">
                        {lang === 'ar' ? 'اختر محادثة' : 'Select a conversation'}
                      </p>
                      <p className="text-sm mt-1">
                        {lang === 'ar' 
                          ? 'اختر محادثة من القائمة لبدء المراسلة'
                          : 'Choose a conversation from the list to start messaging'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MessagesPage;
